import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const icons = {
  success: <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />,
  error: <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />,
  warning: <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />,
  info: <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />,
};

const bgColors = {
  success: 'bg-emerald-50/95 border-emerald-200/80 text-emerald-950 shadow-lg shadow-emerald-900/5',
  error: 'bg-red-50/95 border-red-200/80 text-red-950 shadow-lg shadow-red-900/5',
  warning: 'bg-amber-50/95 border-amber-200/80 text-amber-950 shadow-lg shadow-amber-900/5',
  info: 'bg-blue-50/95 border-blue-200/80 text-blue-950 shadow-lg shadow-blue-900/5',
};

const progressColors = {
  success: 'bg-emerald-500',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
};

const ToastItem = ({ toast, removeToast }) => {
  const { id, message, type, duration } = toast;

  useEffect(() => {
    const timer = setTimeout(() => {
      removeToast(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, removeToast]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95, transition: { duration: 0.15 } }}
      className={`pointer-events-auto relative flex items-start gap-2.5 sm:gap-3 p-3.5 sm:p-4 border rounded-xl sm:rounded-2xl shadow-xl backdrop-blur-md overflow-hidden w-full max-w-full sm:max-w-sm ${bgColors[type]}`}
    >
      {icons[type]}
      <div className="flex-1 text-xs sm:text-sm font-semibold pr-2 leading-relaxed break-words text-gray-800">
        {message}
      </div>
      <button
        type="button"
        onClick={() => removeToast(id)}
        className="text-gray-400 hover:text-gray-600 transition-colors shrink-0 p-1 rounded-full hover:bg-black/5 active:scale-95 cursor-pointer"
        aria-label="Close notification"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Progress Bar indicating time left */}
      <motion.div
        initial={{ width: '100%' }}
        animate={{ width: '0%' }}
        transition={{ duration: duration / 1000, ease: 'linear' }}
        className={`absolute bottom-0 left-0 h-1 ${progressColors[type]}`}
      />
    </motion.div>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 sm:top-20 left-3 right-3 sm:left-auto sm:right-5 z-[99999] flex flex-col items-center sm:items-end gap-2.5 pointer-events-none w-auto max-w-full sm:w-full">
      <AnimatePresence mode="pop-layout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} removeToast={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default ToastContainer;
